import { EventEmitter, Injectable, OnDestroy } from '@angular/core';
import { BusinessCode } from '@data/models/accounting-sage.model';
import { App } from '@data/models/app.model';
import { Authorization } from '@data/models/authorization.model';
import { EmbedCell } from '@data/models/cell.model';
import { Company, EmbedCompany } from '@data/models/company.model';
import { Gdpr } from '@data/models/gdpr.dto';
import { EmbedProject, Project, ProjectServiceCatalog } from '@data/models/project.model';
import { Report } from '@data/models/report.model';
import { Role } from '@data/models/role.model';
import { Period } from '@data/models/sla-period.model';
import { User } from '@data/models/user.model';
import { AccountingSageService } from '@data/services/accounting-sage.service';
import { AppService } from '@data/services/app.service';
import { AuthorizationService } from '@data/services/authorization.service';
import { BusinessProjectService } from '@data/services/business-project.service';
import { CellService } from '@data/services/cell.service';
import { CompanyService } from '@data/services/company.service';
import { GdprService } from '@data/services/gdpr.service';
import { ProjectService } from '@data/services/project.service';
import { ReportService } from '@data/services/report.service';
import { RoleService } from '@data/services/role.service';
import { SlaPeriodService } from '@data/services/sla-period.service';
import { UserService } from '@data/services/user.service';
import { environment } from '@env/environment';
import { cloneDeep, Dictionary, groupBy, isEmpty } from 'lodash';
import { forkJoin, merge, Observable, of, ReplaySubject, zip } from 'rxjs';
import { catchError, filter, map, mergeMap, shareReplay, switchMap } from 'rxjs/operators';

@Injectable({
    providedIn: 'root',
})
export class ProjectWizardCacheService implements OnDestroy {

    private _projectId: string;
    private project$: Observable<EmbedProject<
    '$apps' |
    '$createdBy' |
    '$updatedBy' |
    '$deletedBy' |
    '$tribe' |
    '$customer' |
    '$cell' |
    '$buildProjectManager' |
    '$deliveryManager' |
    '$backupDeliveryManager' |
    '$appManagers'
    >>;
    private relatedCompanies$: Observable<EmbedCompany<'$salesManager'>[]>;
    private customer$: Observable<Company>;
    private partners$: Observable<Company[]>;
    private businessCode$: Observable<BusinessCode>;
    private cell$: Observable<EmbedCell<'$tribe'>>;
    private hasRelatedProjects$: Observable<boolean>;
    private catalogs$: Observable<ProjectServiceCatalog[]>;
    private periods$: Observable<Dictionary<Period>>;
    private users$: Observable<User[]>;
    private authorizations$: Observable<Authorization[]>;
    private roles$: Observable<Role[]>;
    private gdpr$: Observable<Gdpr | undefined>;
    private apps$: Observable<Array<{ group: string; apps: App[] }>>;
    private reports$: Observable<Report[]>;
    private projectReports$: Observable<Report[]>;
    private customerManual$: ReplaySubject<Company>;

    private initialAuthorizations$: Observable<Authorization[]>;
    private initialGdpr$: Observable<Gdpr | undefined>;

    private _loadedTabs: { [key: number]: boolean } = {};

    private _copyFromProjectEvent = new EventEmitter<undefined>();
    private _customerUpdated = new EventEmitter<EmbedCompany<'$salesManager'>>();
    private _relatedCompaniesUpdated = new EventEmitter<Company[]>();

    constructor(
        private projectService: ProjectService,
        private companyService: CompanyService,
        private businessProjectService: BusinessProjectService,
        private accountingSageService: AccountingSageService,
        private cellService: CellService,
        private slaPeriodService: SlaPeriodService,
        private userService: UserService,
        private authorizationService: AuthorizationService,
        private roleService: RoleService,
        private gdprService: GdprService,
        private appService: AppService,
        private reportSerice: ReportService,
    ) { }

    public get loadedTabs(): { [key: number]: boolean } {
        return this._loadedTabs;
    }

    public get copyFromProjectEvent(): EventEmitter<undefined> {
        return this._copyFromProjectEvent;
    }


    public get customerUpdated(): EventEmitter<EmbedCompany<'$salesManager'>> {
        return this._customerUpdated;
    }

    public get relatedCompaniesUpdated(): EventEmitter<Company[]> {
        return this._relatedCompaniesUpdated;
    }

    public get projectId(): string {
        return this._projectId;
    }

    public get project(): Observable<Partial<EmbedProject<
    '$apps' |
    '$createdBy' |
    '$updatedBy' |
    '$deletedBy' |
    '$tribe' |
    '$customer' |
    '$cell' |
    '$buildProjectManager' |
    '$deliveryManager' |
    '$backupDeliveryManager' |
    '$appManagers'
    >>> {
        return this.project$;
    }

    public get customer(): Observable<EmbedCompany<'$salesManager'>> {
        return this.customer$;
    }

    public get partners(): Observable<EmbedCompany<'$salesManager'>[]> {
        return this.partners$;
    }

    public get businessCode(): Observable<BusinessCode> {
        return this.businessCode$;
    }

    public get cell(): Observable<EmbedCell<'$tribe'>> {
        return this.cell$;
    }

    public get hasRelatedProjects(): Observable<boolean> {
        return this.hasRelatedProjects$;
    }

    public get catalogs(): Observable<ProjectServiceCatalog[]> {
        return this.catalogs$;
    }

    public get periods(): Observable<Dictionary<Period>> {
        return this.periods$;
    }

    public get users(): Observable<User[]> {
        return this.users$;
    }

    public get authorizations(): Observable<Authorization[]> {
        return this.authorizations$;
    }

    public get roles(): Observable<Role[]> {
        return this.roles$;
    }

    public get gdpr(): Observable<Gdpr | undefined> {
        return this.gdpr$;
    }

    public get apps(): Observable<Array<{ group: string; apps: App[] }>> {
        return this.apps$;
    }

    public get reports(): Observable<Report[]> {
        return this.reports$;
    }

    public get projectReports(): Observable<Report[]> {
        return this.projectReports$;
    }

    public get initialAuthorizations(): Observable<Authorization[]> {
        return this.initialAuthorizations$;
    }

    public get initialGdpr(): Observable<Gdpr | undefined> {
        return this.initialGdpr$;
    }

    public get relatedCompanies(): Observable<EmbedCompany<'$salesManager'>[]> {
        return this.relatedCompanies$;
    }

    public set users(users: Observable<User[]>) {
        this.users$ = users;
    }

    public set authorizations(authorizations: Observable<Authorization[]>) {
        this.authorizations$ = authorizations;
    }

    // Init data when loading the form
    public initData(projectId: string): void {
        this._projectId = projectId;
        this.customerManual$ = new ReplaySubject<Company>(1);
        this.roles$ = this.roleService.getRoles(false).pipe(
            shareReplay(1),
        );
        this.apps$ = this.appService.findApps({ fields: 'id,name,logo,category,notes' }).pipe(
            map((applications) => {
                return Object.entries(groupBy(applications, ({ category }) => category)).map(([group, apps]) => ({ group, apps }));
            }),
            shareReplay(1),
        );

        this.loadDataFromProject(projectId);

        this.initialAuthorizations$ = this.authorizations$.pipe(
            shareReplay(1),
        );
        this.initialGdpr$ = this.gdpr$;
        this._loadedTabs = {};
    }

    // Set all observable with request for this project
    // Can be called when loading the form or copying data of another project
    public loadDataFromProject(projectId: string, options: {isCopy: boolean} = {isCopy: false}): void {
        this.project$ = (projectId ? this.projectService.getProject(projectId, {
            embedFields: [
                '$createdBy',
                '$updatedBy',
                '$deletedBy',
                '$tribe',
                '$customer',
                '$cell',
                '$buildProjectManager',
                '$deliveryManager',
                '$backupDeliveryManager',
                '$appManagers'
            ],
            includeRemoved: true,
        }) : of({})).pipe(
            shareReplay(1),
        );
        this.relatedCompanies$ = this.project$.pipe(
            mergeMap((project) => {
                return project.customer ? this.companyService.findCompanies({
                    filter: `id:(${[project.customer, ...project.partners].join('|')})`
                }, ['$salesManager']) : of([]);
            }),
            shareReplay(1),
        );

        this.partners$ = forkJoin([this.project$, this.relatedCompanies$]).pipe(
            mergeMap(([project, companies]) => {
                return of(companies.filter((company) => project.partners.includes(company.id)));
            }),
            shareReplay(1),
        );
        this.businessCode$ = this.project$.pipe(
            mergeMap((project) => {
                if (!project.businessCode) {
                    return of(undefined);
                }
                return forkJoin([
                    this.businessProjectService.getBusinessCode(project.businessCode),
                    this.accountingSageService.getBusinessCode(project.businessCode),
                ]).pipe(
                    mergeMap(([businessCodeFromSalesForce, businessCodeFromSage]) => {
                        return of(businessCodeFromSalesForce ?? businessCodeFromSage ?? undefined);
                    }),
                );
            }),
            catchError(() => (of(undefined))),
            shareReplay(1),
        );
        this.cell$ = this.project$.pipe(
            mergeMap((project) => {
                return this.cellService.getEmbeddedCell(project.cell, ['$tribe']);
            }),
            shareReplay(1),
        );
        this.catalogs$ = (projectId ? this.projectService.getProjectServiceCatalogs(projectId) : of([])).pipe(
            shareReplay(1),
        );
        this.periods$ = this.project$.pipe(
            mergeMap((project) => {
                if (!project.id) {
                    return of([]);
                }
                const values = [project.periodReplyTime, project.periodResponseTime].filter(Boolean);
                if (isEmpty(values)) {
                    return of([]);
                }
                return this.slaPeriodService.listPeriods({ filter: `id:(${values.join('|')})` });
            }),
            mergeMap((periods) => {
                return of(periods.reduce((acc, key) => {
                    acc[key.id] = key;
                    return acc;
                }, {}));
            }),
            shareReplay(1),
        );
        this.authorizations$ = (projectId ? forkJoin([
            this.authorizationService.getRolesByProject(projectId),
            this.authorizationService.getCommunicationsByProject(projectId),
        ]) : of([[], []])).pipe(
            mergeMap(([projectRoles, projectCommunications]) => {
                return of(this.authorizationService.getProjectAuthorizations(
                    projectRoles,
                    projectCommunications
                ));
            }),
            shareReplay(1),
            mergeMap((authorizations) => {
                if (options.isCopy) {
                    return of(cloneDeep(authorizations.filter(auth => !auth.roles.includes('bot'))));
                }
                return of(cloneDeep(authorizations));
            }),
        );
        this.hasRelatedProjects$ = this.project$.pipe(
            mergeMap((project) => {
                return this.loadRelatedProjects(project.customer);
            }),
            shareReplay(1),
        );
        this.users$ = this.relatedCompanies$.pipe(
            mergeMap((companies) => {
                return this.loadUsers(companies.map(c => c.id));
            }),
            shareReplay(1),
        );
        this.gdpr$ = this.loadGdpr(projectId);

        const customerOnProjectLoaded$ = zip(this.project$, this.relatedCompanies$).pipe(
            filter(([project, companies]) => Boolean(Object.keys(project).length) && Boolean(companies.length)),
            map(([project, companies]) => {
                return companies.find((company) => company.id === project.customer);
            }),
        );
        this.customer$ = merge(this.customerManual$, customerOnProjectLoaded$);

        this.reports$ = this.customer$.pipe(
            switchMap(customer => {
                return this.reportSerice.getReports({
                    filter: `-app:${environment.oneviewAppId} company:${customer.id}`,
                    fields: 'id,app,authorizedRoles,name,parameters',
                    limit: 500,
                    sort: 'name',
                }, ['$app']);
            }),
            shareReplay(1)
        );

        this.projectReports$ = (projectId ? this.customer$.pipe(
            switchMap(customer => {
                return this.reportSerice.getReports({
                    filter: `projects:${projectId} company:${customer.id}`,
                    fields: 'id',
                    limit: 500,
                    sort: 'name',
                });
            })
        ) : of([])).pipe(
            shareReplay(1),
        );
    }

    // Update data with the new project and tell all tabs to update their contents with copied values
    public copyFromProject(projectId: string): void {
        this.loadDataFromProject(projectId, { isCopy: true });
        this.project$ = this.project$.pipe(
            map((project) => {
                return { ...project, documentUri: '' };
            })
        );
        this._copyFromProjectEvent.emit();
    }

    public updateCustomer(customer: EmbedCompany<'$salesManager'>): void {
        this.customer$ = of(customer);
        this.hasRelatedProjects$ = this.loadRelatedProjects(customer.id);
        this._customerUpdated.emit(customer);
    }

    public updateRelatedCompanies(relatedCompanies: Company[]): void {
        this.users$ = this.loadUsers(relatedCompanies.map(c => c.id));
        this._relatedCompaniesUpdated.emit(relatedCompanies);
    }

    public copyGdprFromProject(projectId: string): void {
        this.gdpr$ = this.loadGdpr(projectId);
    }

    public setTabAsLoaded(tab: number, value: boolean = true): void {
        this._loadedTabs[tab] = value;
    }

    public getRelatedProjects(safeTerm: string): Observable<Project[]> {
        const term = safeTerm.length > 0 ? ` ${safeTerm.trim()}` : '';
        return this.customer$.pipe(mergeMap((customer) => {
            return this.projectService.findProjectsByCompany
            (customer.id, { sort: 'name', filter: this.projectId ? `-id:${this.projectId}${term}` : term.trim() }).pipe(
                mergeMap(projects => {
                    return of(projects);
                }),
            );
        }));
    }

    public setCustomer(customer: Company): void {
        this.customerManual$.next(customer);
    }

    ngOnDestroy(): void {
        this.customerManual$.unsubscribe();
    }

    private loadRelatedProjects(customerId: string): Observable<boolean> {
        return this.projectService.findProjectsByCompany(customerId, {
            limit: 1,
            filter: this.projectId ? `-id:${this.projectId}` : ''
        }).pipe(
            mergeMap(projects => {
                return of(projects.length > 0);
            }),
            shareReplay(1),
        );
    }

    private loadUsers(companies: string[]): Observable<User[]> {
        if (companies.length === 0) {
            return of([]);
        }
        return this.userService.fetchUnpaginatedUsers({ filter: `isInternal:false company:(${companies.join('|')})` }).pipe(
            shareReplay(1),
        );
    }

    private loadGdpr(projectId: string): Observable<Gdpr | undefined> {
        return (projectId ? this.gdprService.getGdpr(projectId) : of(undefined)).pipe(
            shareReplay(1),
        );
    }
}
